import { getMapboxToken } from './mapService';
import supabase from './supabase';

// Route Generation Service
export class RouteGenerationService {
  // Generate optimized route path between multiple points
  static async generateRoutePath(points, transportMode = 'driving') {
    try {
      if (!points || points.length < 2) {
        throw new Error('At least 2 points are required to generate a route');
      }

      const token = getMapboxToken();
      if (!token) {
        throw new Error('Mapbox token not available');
      }

      // Filter out points without coordinates
      const validPoints = points.filter(point => point.coordinates && Array.isArray(point.coordinates));
      
      if (validPoints.length < 2) {
        throw new Error('At least 2 points with coordinates are required');
      }

      // Create waypoints string for Mapbox Directions API
      const waypoints = validPoints
        .map(point => `${point.coordinates[0]},${point.coordinates[1]}`)
        .join(';');

      const profile = transportMode === 'driving' ? 'driving-traffic' : transportMode;
      const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${waypoints}?geometries=geojson&overview=full&steps=true&access_token=${token}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Directions API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.routes || data.routes.length === 0) {
        throw new Error('No route found between the specified points');
      }

      const route = data.routes[0];
      
      const legDurations = Array.isArray(route.legs) ? route.legs.map(l => l?.duration || 0) : [];
      return {
        geometry: route.geometry,
        distance: route.distance, // in meters
        duration: route.duration, // in seconds
        legs: route.legs,
        legDurations,
        waypoint_order: route.waypoint_order || []
      };
    } catch (error) {
      console.error('Error generating route path:', error);
      throw error;
    }
  }

  // Save route with generated geometry
  static async saveRouteWithGeometry(routeData, routeGeometry) {
    try {
      const routeWithGeometry = {
        ...routeData,
        route_geometry: routeGeometry.geometry,
        total_distance: routeGeometry.distance,
        total_duration: routeGeometry.duration,
        transport_mode: routeData.transport_mode || 'driving'
      };

      const { data, error } = await supabase
        .from('routes')
        .insert([routeWithGeometry])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving route with geometry:', error);
      throw error;
    }
  }

  // Get route directions for display
  static async getRouteDirections(routeId) {
    try {
      const { data: route, error } = await supabase
        .from('routes')
        .select('route_geometry, total_distance, total_duration, transport_mode')
        .eq('id', routeId)
        .single();

      if (error) throw error;
      return route;
    } catch (error) {
      console.error('Error fetching route directions:', error);
      throw error;
    }
  }

  // Generate and save complete route with points and geometry
  static async generateAndSaveCompleteRoute(routeData, points, transportMode = 'driving') {
    try {
      // Generate route path
      const routeGeometry = await this.generateRoutePath(points, transportMode);
      
      // Save route with geometry
      const route = await this.saveRouteWithGeometry(routeData, routeGeometry);
      
      // Save route points
      const pointsToInsert = points.map((point, idx) => ({
        route_id: route.id,
        point_order: idx,
        name: point.name || '',
        location_search: point.location || '',
        latitude: point.coordinates ? point.coordinates[1] : null,
        longitude: point.coordinates ? point.coordinates[0] : null,
        tags: Array.isArray(point.tags) ? point.tags : [],
        notes: point.notes || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      const { error: pointsError } = await supabase
        .from('route_points')
        .insert(pointsToInsert);

      if (pointsError) throw pointsError;

      return {
        route,
        geometry: routeGeometry,
        points: pointsToInsert
      };
    } catch (error) {
      console.error('Error generating and saving complete route:', error);
      throw error;
    }
  }

  // Format distance for display
  static formatDistance(meters) {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    }
    return `${(meters / 1000).toFixed(1)}km`;
  }

  // Format duration for display
  static formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  // Get transport mode icon
  static getTransportModeIcon(mode) {
    const icons = {
      driving: '🚗',
      walking: '🚶',
      cycling: '🚴',
      transit: '🚌'
    };
    return icons[mode] || '🚗';
  }

  // Get transport mode label
  static getTransportModeLabel(mode) {
    const labels = {
      driving: 'Driving',
      walking: 'Walking',
      cycling: 'Cycling',
      transit: 'Transit'
    };
    return labels[mode] || 'Driving';
  }
}

